import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

const baseUrl =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:50268";

export const api = createApi({
  reducerPath: "api",
  baseQuery: fetchBaseQuery({
    baseUrl,
    prepareHeaders: (headers, { getState }) => {
      const token = getState().auth.token;
      if (token) {
        headers.set("authorization", `Bearer ${token}`);
      }
      const deviceToken =
        localStorage.getItem("edgeDeviceToken") ||
        process.env.REACT_APP_DEVICE_TOKEN ||
        "";
      if (deviceToken) {
        headers.set("X-DEVICE-TOKEN", deviceToken);
      }
      return headers;
    },
  }),
  tagTypes: [
    "Category",
    "Item",
    "Station",
    "User",
    "AccessLevel",
    "Menu",
    "Floor",
    "Section",
    "Table",
    "Device",
    "Printer",
    "TaxRate",
    "Report",
    "UpsellCampaign",
    "FulfillmentSettings",
    "FulfillmentOrder",
    "Seat",
    "PosOrder",
    "Invoice",
    "Payment",
    "EdgeStoreSetup",
    "StoreFeatures",
    "Reservation",
    "Till",
    "TillSession",
    "CashMovement",
    "Reconciliation",
    "DayClose",
  ],
  endpoints: (builder) => ({
    // Auth
    loginWithPin: builder.mutation({
      query: (payload) => ({
        url: "/api/edge/users/pin/login",
        method: "POST",
        body: payload,
      }),
    }),
    getEdgeCustomerLookup: builder.query({
      query: (mobile) => {
        const params = new URLSearchParams();
        if (mobile) params.append("mobile", mobile);
        return `/api/edge/customers/lookup?${params.toString()}`;
      },
    }),
    createRealtimeSession: builder.mutation({
      query: (payload) => ({
        url: "/api/edge/realtime/session",
        method: "POST",
        body: payload,
      }),
    }),
    getRealtimeSync: builder.query({
      query: ({ since, scope = "STORE" } = {}) => {
        const params = new URLSearchParams();
        if (since) params.append("since", since);
        if (scope) params.append("scope", scope);
        return `/api/edge/realtime/sync?${params.toString()}`;
      },
    }),
    getEdgeStoreSetup: builder.query({
      query: () => "/api/edge/setup/store",
      providesTags: ["EdgeStoreSetup"],
    }),
    getStoreFeatures: builder.query({
      query: (storeId) => {
        const params = new URLSearchParams();
        if (storeId) params.append("storeId", storeId);
        const queryString = params.toString();
        return queryString
          ? `/api/edge/stores/features?${queryString}`
          : "/api/edge/stores/features";
      },
      providesTags: ["StoreFeatures"],
    }),
    updateStoreFeatures: builder.mutation({
      query: ({ storeId, payload }) => {
        const params = new URLSearchParams();
        if (storeId) params.append("storeId", storeId);
        const queryString = params.toString();
        return {
          url: queryString
            ? `/api/edge/stores/features?${queryString}`
            : "/api/edge/stores/features",
          method: "PUT",
          body: payload,
        };
      },
      invalidatesTags: ["StoreFeatures"],
    }),
    configureEdgeStore: builder.mutation({
      query: (payload) => ({
        url: "/api/edge/setup/store",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["EdgeStoreSetup"],
    }),
    bridgePaymentCallback: builder.mutation({
      query: ({ payload, signature, eventId }) => ({
        url: "/api/edge/bridge/payments/callback",
        method: "POST",
        body: payload,
        headers: {
          ...(signature ? { "X-BRIDGE-SIGNATURE": signature } : {}),
          ...(eventId ? { "X-BRIDGE-EVENT-ID": eventId } : {}),
        },
      }),
    }),
    bridgePrintCallback: builder.mutation({
      query: ({ payload, signature, eventId }) => ({
        url: "/api/edge/bridge/print-jobs/callback",
        method: "POST",
        body: payload,
        headers: {
          ...(signature ? { "X-BRIDGE-SIGNATURE": signature } : {}),
          ...(eventId ? { "X-BRIDGE-EVENT-ID": eventId } : {}),
        },
      }),
    }),

    // Reservations
    getReservations: builder.query({
      query: ({ date, status, search } = {}) => {
        const params = new URLSearchParams();
        if (date) params.append("date", date);
        if (status) params.append("status", status);
        if (search) params.append("search", search);
        const queryString = params.toString();
        return queryString
          ? `/api/edge/reservations?${queryString}`
          : "/api/edge/reservations";
      },
      providesTags: ["Reservation"],
    }),
    getReservationSlots: builder.query({
      query: ({ date }) => `/api/edge/reservations/slots?date=${encodeURIComponent(date)}`,
      providesTags: ["Reservation"],
    }),
    getReservationByCode: builder.query({
      query: (code) => `/api/edge/reservations/by-code/${encodeURIComponent(code)}`,
      providesTags: ["Reservation"],
    }),
    getEdgeCustomers: builder.query({
      query: ({ search, page = 1, pageSize = 50 } = {}) => {
        const params = new URLSearchParams();
        if (search) params.append("search", search);
        params.append("page", page);
        params.append("pageSize", pageSize);
        return `/api/edge/customers?${params.toString()}`;
      },
      providesTags: ["Reservation"],
    }),
    createReservation: builder.mutation({
      query: (payload) => ({
        url: "/api/edge/reservations",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["Reservation"],
    }),
    updateReservation: builder.mutation({
      query: ({ reservationId, payload }) => ({
        url: `/api/edge/reservations/${reservationId}`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["Reservation"],
    }),
    assignReservationTable: builder.mutation({
      query: ({ reservationId, tableId }) => ({
        url: `/api/edge/reservations/${reservationId}/assign-table`,
        method: "POST",
        body: { tableId },
      }),
      invalidatesTags: ["Reservation", "Table"],
    }),
    checkInReservation: builder.mutation({
      query: (reservationId) => ({
        url: `/api/edge/reservations/${reservationId}/checkin`,
        method: "POST",
      }),
      invalidatesTags: ["Reservation"],
    }),
    markNoShowReservation: builder.mutation({
      query: (reservationId) => ({
        url: `/api/edge/reservations/${reservationId}/no-show`,
        method: "POST",
      }),
      invalidatesTags: ["Reservation"],
    }),
    markUsedReservation: builder.mutation({
      query: (reservationId) => ({
        url: `/api/edge/reservations/${reservationId}/used`,
        method: "POST",
      }),
      invalidatesTags: ["Reservation"],
    }),
    cancelReservation: builder.mutation({
      query: ({ reservationId, reason }) => ({
        url: reason
          ? `/api/edge/reservations/${reservationId}/cancel?reason=${encodeURIComponent(reason)}`
          : `/api/edge/reservations/${reservationId}/cancel`,
        method: "POST",
      }),
      invalidatesTags: ["Reservation"],
    }),

    // Categories
    getCategories: builder.query({
      query: () => "/admin/catalog/categories",
      providesTags: ["Category"],
    }),
    getStations: builder.query({
      query: () => "/admin/catalog/stations",
      providesTags: ["Station"],
    }),
    createStation: builder.mutation({
      query: (payload) => ({
        url: "/admin/catalog/stations",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["Station", "Device"],
    }),
    updateStation: builder.mutation({
      query: ({ stationId, payload }) => ({
        url: `/admin/catalog/stations/${stationId}`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["Station", "Device"],
    }),
    deactivateStation: builder.mutation({
      query: (stationId) => ({
        url: `/admin/catalog/stations/${stationId}/deactivate`,
        method: "POST",
      }),
      invalidatesTags: ["Station"],
    }),

    // Printers (Edge)
    getPrinters: builder.query({
      query: () => "/api/edge/printers",
      providesTags: ["Printer"],
    }),
    createPrinter: builder.mutation({
      query: (payload) => ({
        url: "/api/edge/printers",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["Printer"],
    }),
    updatePrinter: builder.mutation({
      query: ({ printerId, payload }) => ({
        url: `/api/edge/printers/${printerId}`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["Printer"],
    }),
    deletePrinter: builder.mutation({
      query: (printerId) => ({
        url: `/api/edge/printers/${printerId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Printer"],
    }),

    // Public Catalog Menu (order-type filtered)
    getPublicCatalogMenu: builder.query({
      query: ({ orderType } = {}) => {
        const params = new URLSearchParams();
        if (orderType) params.append("orderType", orderType);
        const queryString = params.toString();
        return queryString
          ? `/public/catalog/menu?${queryString}`
          : "/public/catalog/menu";
      },
      providesTags: ["Menu"],
    }),
    createCategory: builder.mutation({
      query: ({ payload, file }) => {
        const formData = new FormData();
        formData.append("Name", payload.name || "");
        formData.append("Description", payload.description || "");
        formData.append("Sort", payload.sort || 0);
        formData.append(
          "IsActive",
          String(payload.isActive !== undefined ? payload.isActive : true),
        );
        if (payload.parentCategoryId) {
          formData.append("ParentCategoryId", payload.parentCategoryId);
        }
        if (payload.defaultStationCode !== undefined) {
          formData.append(
            "defaultStationCode",
            payload.defaultStationCode || "",
          );
        }
        if (file) {
          formData.append("File", file);
        }
        return {
          url: "/admin/catalog/categories",
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: ["Category"],
    }),
    updateCategory: builder.mutation({
      query: ({ categoryId, payload, file, removeImage }) => {
        const formData = new FormData();
        formData.append("Name", payload.name || "");
        formData.append("Description", payload.description || "");
        formData.append("Sort", payload.sort || 0);
        formData.append(
          "IsActive",
          String(payload.isActive !== undefined ? payload.isActive : true),
        );
        if (payload.parentCategoryId) {
          formData.append("ParentCategoryId", payload.parentCategoryId);
        }
        if (payload.defaultStationCode !== undefined) {
          formData.append(
            "defaultStationCode",
            payload.defaultStationCode || "",
          );
        }
        if (file) {
          formData.append("File", file);
        }
        if (removeImage) {
          formData.append("RemoveImage", "true");
        }
        return {
          url: `/admin/catalog/categories/${categoryId}`,
          method: "PUT",
          body: formData,
        };
      },
      invalidatesTags: ["Category"],
    }),
    deleteCategory: builder.mutation({
      query: (categoryId) => ({
        url: `/admin/catalog/categories/${categoryId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Category"],
    }),

    // Items
    getItems: builder.query({
      query: (params = {}) => {
        if (!params || typeof params !== "object" || Array.isArray(params)) {
          return "/admin/catalog/items";
        }

        const query = new URLSearchParams();
        const {
          effectiveStationCode,
          hasStationCode,
          search,
          categoryId,
          page,
          pageSize,
        } = params;

        if (effectiveStationCode) {
          query.append("effectiveStationCode", effectiveStationCode);
        }
        if (hasStationCode !== undefined && hasStationCode !== null) {
          query.append("hasStationCode", String(Boolean(hasStationCode)));
        }
        if (search) {
          query.append("search", search);
        }
        if (categoryId) {
          query.append("categoryId", categoryId);
        }
        if (page) {
          query.append("page", page);
        }
        if (pageSize) {
          query.append("pageSize", pageSize);
        }

        const queryString = query.toString();
        return queryString
          ? `/admin/catalog/items?${queryString}`
          : "/admin/catalog/items";
      },
      providesTags: ["Item"],
    }),
    createItem: builder.mutation({
      query: ({ payload, file }) => {
        const formData = new FormData();
        formData.append("Name", payload.name || "");
        formData.append("Description", payload.description || "");
        formData.append("BasePrice", payload.basePrice || 0);
        formData.append("CategoryId", payload.categoryId || "");
        if (payload.sku) {
          formData.append("Sku", payload.sku);
        }
        if (payload.stationCode !== undefined) {
          formData.append("stationCode", payload.stationCode || "");
        }
        if (payload.imageUrl && !file) {
          formData.append("imageUrl", payload.imageUrl);
        }
        formData.append(
          "IsAvailable",
          String(
            payload.isAvailable !== undefined ? payload.isAvailable : true,
          ),
        );
        formData.append(
          "IsTaxable",
          String(payload.isTaxable !== undefined ? payload.isTaxable : true),
        );
        formData.append("IsVegetarian", String(payload.isVegetarian || false));
        formData.append("IsVegan", String(payload.isVegan || false));
        formData.append("IsSpicy", String(payload.isSpicy || false));
        if (payload.spiceLevel !== null && payload.spiceLevel !== undefined) {
          formData.append("SpiceLevel", payload.spiceLevel);
        }
        formData.append(
          "ContainsAlcohol",
          String(payload.containsAlcohol || false),
        );
        formData.append("IsHalal", String(payload.isHalal || false));
        formData.append("IsKosher", String(payload.isKosher || false));
        if (payload.proteinTypeIdsJson !== undefined) {
          formData.append("proteinTypeIdsJson", payload.proteinTypeIdsJson);
        }
        if (payload.allergensJson !== undefined) {
          formData.append("allergensJson", payload.allergensJson);
        }
        if (payload.modifierGroupsJson !== undefined) {
          formData.append("modifierGroupsJson", payload.modifierGroupsJson);
        }
        if (file) {
          formData.append("File", file);
        }
        return {
          url: "/admin/catalog/items",
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: ["Item"],
    }),
    updateItem: builder.mutation({
      query: ({ itemId, payload, file, removeImage }) => {
        const formData = new FormData();
        formData.append("Name", payload.name || "");
        formData.append("Description", payload.description || "");
        formData.append("BasePrice", payload.basePrice || 0);
        formData.append("CategoryId", payload.categoryId || "");
        if (payload.sku) {
          formData.append("Sku", payload.sku);
        }
        if (payload.stationCode !== undefined) {
          formData.append("stationCode", payload.stationCode || "");
        }
        if (payload.imageUrl && !file) {
          formData.append("imageUrl", payload.imageUrl);
        }
        if (payload.isAvailable !== undefined) {
          formData.append("IsAvailable", String(payload.isAvailable));
        }
        if (payload.isTaxable !== undefined) {
          formData.append("IsTaxable", String(payload.isTaxable));
        }
        if (payload.isVegetarian !== undefined) {
          formData.append("IsVegetarian", String(payload.isVegetarian));
        }
        if (payload.isVegan !== undefined) {
          formData.append("IsVegan", String(payload.isVegan));
        }
        if (payload.isSpicy !== undefined) {
          formData.append("IsSpicy", String(payload.isSpicy));
        }
        if (payload.spiceLevel !== null && payload.spiceLevel !== undefined) {
          formData.append("SpiceLevel", payload.spiceLevel);
        }
        if (payload.containsAlcohol !== undefined) {
          formData.append("ContainsAlcohol", String(payload.containsAlcohol));
        }
        if (payload.isHalal !== undefined) {
          formData.append("IsHalal", String(payload.isHalal));
        }
        if (payload.isKosher !== undefined) {
          formData.append("IsKosher", String(payload.isKosher));
        }
        if (payload.proteinTypeIdsJson !== undefined) {
          formData.append("proteinTypeIdsJson", payload.proteinTypeIdsJson);
        }
        if (payload.allergensJson !== undefined) {
          formData.append("allergensJson", payload.allergensJson);
        }
        if (payload.modifierGroupsJson !== undefined) {
          formData.append("modifierGroupsJson", payload.modifierGroupsJson);
        }
        if (file) {
          formData.append("File", file);
        }
        if (removeImage) {
          formData.append("RemoveImage", "true");
        }
        return {
          url: `/admin/catalog/items/${itemId}`,
          method: "PUT",
          body: formData,
        };
      },
      invalidatesTags: ["Item"],
    }),
    // Item availability toggle (Edge local operational control)
    patchItemAvailability: builder.mutation({
      query: ({ itemId, isAvailable }) => ({
        url: `/admin/catalog/items/${itemId}/availability`,
        method: "PATCH",
        body: { isAvailable },
      }),
      invalidatesTags: ["Item"],
    }),
    patchCategorySortOrder: builder.mutation({
      query: ({ categoryId, sortOrder }) => ({
        url: `/admin/catalog/categories/${categoryId}/sort-order`,
        method: "PATCH",
        body: { sortOrder },
      }),
      invalidatesTags: ["Category"],
    }),
    patchItemSortOrder: builder.mutation({
      query: ({ itemId, sortOrder }) => ({
        url: `/admin/catalog/items/${itemId}/sort-order`,
        method: "PATCH",
        body: { sortOrder },
      }),
      invalidatesTags: ["Item"],
    }),
    assignItemStation: builder.mutation({
      query: ({ itemId, stationCode, requestId }) => ({
        url: `/admin/catalog/items/${itemId}/station`,
        method: "PATCH",
        body: {
          stationCode,
          ...(requestId ? { requestId } : {}),
        },
      }),
      invalidatesTags: ["Item", "Station"],
    }),
    batchAssignItemStations: builder.mutation({
      query: ({ operations, requestId }) => ({
        url: "/admin/catalog/items/station-assignments:batch",
        method: "POST",
        body: {
          operations,
          ...(requestId ? { requestId } : {}),
        },
      }),
      invalidatesTags: ["Item", "Station"],
    }),
    deleteItem: builder.mutation({
      query: (itemId) => ({
        url: `/admin/catalog/items/${itemId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Item"],
    }),

    // Menu
    getMenu: builder.query({
      query: () => "/admin/catalog/menu",
      providesTags: ["Menu"],
    }),
    getCatalogReference: builder.query({
      query: () => "/admin/catalog/reference",
      providesTags: ["Category", "Item"],
    }),

    // Users
    getUsers: builder.query({
      query: () => "/api/edge/users",
      providesTags: ["User"],
    }),
    getUser: builder.query({
      query: (userId) => `/api/edge/users/${userId}`,
      providesTags: ["User"],
    }),
    createUser: builder.mutation({
      query: (payload) => ({
        url: "/api/edge/users",
        method: "POST",
        body: {
          displayName: payload.displayName,
          pin: payload.pin,
          accessLevelId: payload.accessLevelId || null,
          isActive: payload.isActive,
        },
      }),
      invalidatesTags: ["User"],
    }),
    updateUser: builder.mutation({
      query: ({ userId, payload }) => ({
        url: `/api/edge/users/${userId}`,
        method: "PUT",
        body: {
          displayName: payload.displayName,
          pin: payload.pin || undefined, // Don't send if empty
          accessLevelId: payload.accessLevelId || null,
          isActive: payload.isActive,
        },
      }),
      invalidatesTags: ["User"],
    }),
    deleteUser: builder.mutation({
      query: (userId) => ({
        url: `/api/edge/users/${userId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["User"],
    }),

    // Access Levels
    getAccessLevels: builder.query({
      query: () => "/api/edge/users/access-levels",
      providesTags: ["AccessLevel"],
    }),
    createAccessLevel: builder.mutation({
      query: (payload) => ({
        url: "/api/edge/users/access-levels",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["AccessLevel"],
    }),
    updateAccessLevel: builder.mutation({
      query: ({ accessLevelId, payload }) => ({
        url: `/api/edge/users/access-levels/${accessLevelId}`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["AccessLevel"],
    }),
    deleteAccessLevel: builder.mutation({
      query: (accessLevelId) => ({
        url: `/api/edge/users/access-levels/${accessLevelId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["AccessLevel"],
    }),

    // Floorplan - Floors
    getFloors: builder.query({
      query: () => "/api/edge/floorplan/floors",
      providesTags: ["Floor"],
    }),
    createFloor: builder.mutation({
      query: (payload) => ({
        url: "/api/edge/floorplan/floors",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["Floor"],
    }),
    updateFloor: builder.mutation({
      query: ({ floorId, payload }) => ({
        url: `/api/edge/floorplan/floors/${floorId}`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["Floor"],
    }),
    deleteFloor: builder.mutation({
      query: (floorId) => ({
        url: `/api/edge/floorplan/floors/${floorId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Floor"],
    }),

    // Floorplan - Sections
    getSections: builder.query({
      query: (floorId) => `/api/edge/floorplan/sections?floorId=${floorId}`,
      providesTags: ["Section"],
    }),
    createSection: builder.mutation({
      query: (payload) => ({
        url: "/api/edge/floorplan/sections",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["Section"],
    }),
    updateSection: builder.mutation({
      query: ({ sectionId, payload }) => ({
        url: `/api/edge/floorplan/sections/${sectionId}`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["Section"],
    }),
    deleteSection: builder.mutation({
      query: (sectionId) => ({
        url: `/api/edge/floorplan/sections/${sectionId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Section"],
    }),

    // Floorplan - Tables
    getTables: builder.query({
      query: (sectionId) =>
        sectionId
          ? `/api/edge/floorplan/tables?sectionId=${sectionId}`
          : "/api/edge/floorplan/tables",
      providesTags: ["Table"],
    }),
    createTable: builder.mutation({
      query: (payload) => ({
        url: "/api/edge/floorplan/tables",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["Table"],
    }),
    updateTable: builder.mutation({
      query: ({ tableId, payload }) => ({
        url: `/api/edge/floorplan/tables/${tableId}`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["Table"],
    }),
    deleteTable: builder.mutation({
      query: (tableId) => ({
        url: `/api/edge/floorplan/tables/${tableId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Table"],
    }),

    // Tax Rates
    getTaxRates: builder.query({
      query: () => "/admin/catalog/tax-rates",
      providesTags: ["TaxRate"],
    }),
    createTaxRate: builder.mutation({
      query: (payload) => ({
        url: "/admin/catalog/tax-rates",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["TaxRate"],
    }),
    updateTaxRate: builder.mutation({
      query: ({ taxRateId, payload }) => ({
        url: `/admin/catalog/tax-rates/${taxRateId}`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["TaxRate"],
    }),
    deleteTaxRate: builder.mutation({
      query: (taxRateId) => ({
        url: `/admin/catalog/tax-rates/${taxRateId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["TaxRate"],
    }),

    // Devices - Local Devices
    getLocalDevices: builder.query({
      query: (type) =>
        `/api/edge/devices/local-devices${type ? `?type=${type}` : ""}`,
      providesTags: ["Device"],
    }),
    getDeviceHealth: builder.query({
      query: ({ type, status } = {}) => {
        const params = new URLSearchParams();
        if (type) params.append("type", type);
        if (status) params.append("status", status);
        const queryString = params.toString();
        return queryString
          ? `/api/edge/devices/health?${queryString}`
          : "/api/edge/devices/health";
      },
      providesTags: ["Device"],
    }),
    postDeviceHeartbeat: builder.mutation({
      query: (deviceId) => ({
        url: `/api/edge/devices/${deviceId}/heartbeat`,
        method: "POST",
      }),
      invalidatesTags: ["Device"],
    }),
    createLocalDevice: builder.mutation({
      query: (payload) => ({
        url: "/api/edge/devices/local-devices",
        method: "POST",
        body: payload,
        headers: {
          "Content-Type": "application/json",
        },
      }),
      invalidatesTags: ["Device"],
    }),
    updateLocalDevice: builder.mutation({
      query: ({ deviceId, payload }) => ({
        url: `/api/edge/devices/local-devices/${deviceId}`,
        method: "PUT",
        body: payload,
        headers: {
          "Content-Type": "application/json",
        },
      }),
      invalidatesTags: ["Device"],
    }),
    deleteLocalDevice: builder.mutation({
      query: (deviceId) => ({
        url: `/api/edge/devices/local-devices/${deviceId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Device"],
    }),

    // Devices - Kiosks
    getKiosks: builder.query({
      query: () => "/api/edge/devices/kiosks",
      providesTags: ["Device"],
    }),
    createKiosk: builder.mutation({
      query: (payload) => ({
        url: "/api/edge/devices/kiosks",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["Device"],
    }),
    updateKiosk: builder.mutation({
      query: ({ kioskId, payload }) => ({
        url: `/api/edge/devices/kiosks/${kioskId}`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["Device"],
    }),
    deleteKiosk: builder.mutation({
      query: (kioskId) => ({
        url: `/api/edge/devices/kiosks/${kioskId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Device"],
    }),
    unlockKiosk: builder.mutation({
      query: (kioskId) => ({
        url: `/api/edge/devices/kiosks/${kioskId}/unlock`,
        method: "POST",
      }),
      invalidatesTags: ["Device"],
    }),
    lockKiosk: builder.mutation({
      query: (kioskId) => ({
        url: `/api/edge/devices/kiosks/${kioskId}/lock`,
        method: "POST",
      }),
      invalidatesTags: ["Device"],
    }),
    getKioskStatus: builder.query({
      query: (kioskId) => `/api/edge/devices/kiosks/${kioskId}/status`,
      providesTags: ["Device"],
    }),

    // Devices - Terminals
    getTerminals: builder.query({
      query: () => "/api/edge/devices/terminals",
      providesTags: ["Device"],
    }),
    createTerminal: builder.mutation({
      query: (payload) => ({
        url: "/api/edge/devices/terminals",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["Device"],
    }),
    updateTerminal: builder.mutation({
      query: ({ terminalId, payload }) => ({
        url: `/api/edge/devices/terminals/${terminalId}`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["Device"],
    }),
    deleteTerminal: builder.mutation({
      query: (terminalId) => ({
        url: `/api/edge/devices/terminals/${terminalId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Device"],
    }),
    assignTerminalToKiosk: builder.mutation({
      query: ({ kioskId, terminalId }) => ({
        url: `/api/edge/devices/kiosks/${kioskId}/terminal/${terminalId}`,
        method: "PUT",
      }),
      invalidatesTags: ["Device"],
    }),

    // Cashing Up - Tills
    getTills: builder.query({
      query: ({ storeId, activeOnly = false } = {}) => {
        if (!storeId) return "/api/stores//tills";
        const params = new URLSearchParams();
        if (activeOnly !== undefined && activeOnly !== null) {
          params.append("activeOnly", String(Boolean(activeOnly)));
        }
        const queryString = params.toString();
        return queryString
          ? `/api/stores/${storeId}/tills?${queryString}`
          : `/api/stores/${storeId}/tills`;
      },
      providesTags: ["Till"],
    }),
    getTill: builder.query({
      query: (tillId) => `/api/tills/${tillId}`,
      providesTags: (result, error, tillId) => [{ type: "Till", id: tillId }],
    }),
    createTill: builder.mutation({
      query: ({ storeId, payload }) => ({
        url: `/api/stores/${storeId}/tills`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["Till"],
    }),
    updateTill: builder.mutation({
      query: ({ tillId, payload }) => ({
        url: `/api/tills/${tillId}`,
        method: "PATCH",
        body: payload,
      }),
      invalidatesTags: (result, error, { tillId }) => [
        "Till",
        { type: "Till", id: tillId },
      ],
    }),
    deactivateTill: builder.mutation({
      query: (tillId) => ({
        url: `/api/tills/${tillId}/deactivate`,
        method: "POST",
      }),
      invalidatesTags: ["Till"],
    }),
    getOpenTillSessions: builder.query({
      query: (storeId) =>
        storeId
          ? `/api/stores/${storeId}/till-sessions/open`
          : "/api/stores//till-sessions/open",
      providesTags: ["TillSession"],
    }),
    getEdgeShiftHistorySessions: builder.query({
      query: ({ fromDate, toDate, tillId, statuses, limit } = {}) => {
        const params = new URLSearchParams();
        if (fromDate) params.append("fromDate", fromDate);
        if (toDate) params.append("toDate", toDate);
        if (tillId) params.append("tillId", tillId);
        if (statuses) params.append("statuses", statuses);
        if (limit !== undefined && limit !== null && limit !== "") {
          params.append("limit", String(limit));
        }
        const queryString = params.toString();
        return queryString
          ? `/api/edge/cashing-up/sessions?${queryString}`
          : "/api/edge/cashing-up/sessions";
      },
      providesTags: ["TillSession"],
    }),
    openTillSession: builder.mutation({
      query: ({ payload }) => ({
        url: "/api/tills/sessions/open",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["TillSession"],
    }),
    getTillSession: builder.query({
      query: (cashSessionId) => `/api/till-sessions/${cashSessionId}`,
      providesTags: (result, error, cashSessionId) => [
        { type: "TillSession", id: cashSessionId },
      ],
    }),
    getCashMovements: builder.query({
      query: (cashSessionId) =>
        `/api/till-sessions/${cashSessionId}/cash-movements`,
      providesTags: (result, error, cashSessionId) => [
        { type: "CashMovement", id: cashSessionId },
      ],
    }),
    createCashMovement: builder.mutation({
      query: ({ cashSessionId, payload }) => ({
        url: `/api/till-sessions/${cashSessionId}/cash-movements`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["CashMovement", "TillSession", "Reconciliation"],
    }),
    updateCashMovement: builder.mutation({
      query: ({ cashMovementId, payload }) => ({
        url: `/api/cash-movements/${cashMovementId}`,
        method: "PATCH",
        body: payload,
      }),
      invalidatesTags: ["CashMovement", "TillSession", "Reconciliation"],
    }),
    deleteCashMovement: builder.mutation({
      query: ({ cashMovementId, deletedByUserId, reason }) => {
        const params = new URLSearchParams();
        if (deletedByUserId) params.append("deletedByUserId", deletedByUserId);
        if (reason) params.append("reason", reason);
        const queryString = params.toString();
        return {
          url: `/api/cash-movements/${cashMovementId}${
            queryString ? `?${queryString}` : ""
          }`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["CashMovement", "TillSession", "Reconciliation"],
    }),
    approveCashMovement: builder.mutation({
      query: ({ cashMovementId, payload }) => ({
        url: `/api/cash-movements/${cashMovementId}/approve`,
        method: "POST",
        body: payload || {},
      }),
      invalidatesTags: ["CashMovement", "TillSession", "Reconciliation"],
    }),
    rejectCashMovement: builder.mutation({
      query: ({ cashMovementId, payload }) => ({
        url: `/api/cash-movements/${cashMovementId}/reject`,
        method: "POST",
        body: payload || {},
      }),
      invalidatesTags: ["CashMovement", "TillSession", "Reconciliation"],
    }),
    closeTillSession: builder.mutation({
      query: ({ cashSessionId, payload }) => ({
        url: `/api/till-sessions/${cashSessionId}/close`,
        method: "POST",
        body: payload || {},
      }),
      invalidatesTags: ["TillSession", "CashMovement", "Reconciliation"],
    }),
    approveTillSession: builder.mutation({
      query: ({ cashSessionId, payload }) => ({
        url: `/api/till-sessions/${cashSessionId}/approve`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["TillSession", "Reconciliation"],
    }),
    reopenTillSession: builder.mutation({
      query: ({ cashSessionId, payload }) => ({
        url: `/api/till-sessions/${cashSessionId}/reopen`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["TillSession", "Reconciliation"],
    }),
    startCashUp: builder.mutation({
      query: ({ cashSessionId, payload }) => ({
        url: `/api/till-sessions/${cashSessionId}/start-cash-up`,
        method: "POST",
        body: payload || {},
      }),
      invalidatesTags: ["TillSession", "Reconciliation"],
    }),
    submitCashCount: builder.mutation({
      query: ({ cashSessionId, payload }) => ({
        url: `/api/till-sessions/${cashSessionId}/cash-count`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["TillSession", "Reconciliation"],
    }),
    getReconciliation: builder.query({
      query: (cashSessionId) =>
        `/api/till-sessions/${cashSessionId}/reconciliation`,
      providesTags: (result, error, cashSessionId) => [
        { type: "Reconciliation", id: cashSessionId },
      ],
    }),
    recalculateReconciliation: builder.mutation({
      query: ({ cashSessionId }) => ({
        url: `/api/till-sessions/${cashSessionId}/reconciliation/recalculate`,
        method: "POST",
      }),
      invalidatesTags: ["Reconciliation", "TillSession"],
    }),
    saveReconciliationLines: builder.mutation({
      query: ({ cashSessionId, payload }) => ({
        url: `/api/till-sessions/${cashSessionId}/reconciliation-lines`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["Reconciliation", "TillSession"],
    }),
    getDayClose: builder.query({
      query: ({ storeId, businessDate }) => {
        if (!storeId || !businessDate) {
          return "/api/stores//day-close/";
        }
        return `/api/stores/${storeId}/day-close/${businessDate}`;
      },
      providesTags: ["DayClose"],
    }),
    prepareDayClose: builder.mutation({
      query: ({ storeId, businessDate }) => ({
        url: `/api/stores/${storeId}/day-close/${businessDate}/prepare`,
        method: "POST",
      }),
      invalidatesTags: ["DayClose"],
    }),
    closeDayClose: builder.mutation({
      query: ({ storeId, businessDate, payload }) => ({
        url: `/api/stores/${storeId}/day-close/${businessDate}/close`,
        method: "POST",
        body: payload || {},
      }),
      invalidatesTags: ["DayClose"],
    }),
    reopenDayClose: builder.mutation({
      query: ({ dayCloseId, payload }) => ({
        url: `/api/day-closes/${dayCloseId}/reopen`,
        method: "POST",
        body: payload || {},
      }),
      invalidatesTags: ["DayClose"],
    }),

    // Device Pairing
    pairDevice: builder.mutation({
      query: (payload) => ({
        url: "/api/edge/devices/pair",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["Device"],
    }),
    getFulfillmentSettings: builder.query({
      query: () => "/api/edge/devices/fulfillment-settings",
      providesTags: ["FulfillmentSettings"],
    }),
    updateFulfillmentSettings: builder.mutation({
      query: (payload) => ({
        url: "/api/edge/devices/fulfillment-settings",
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["FulfillmentSettings"],
    }),
    uploadOrderDisplayBackground: builder.mutation({
      query: (file) => {
        const formData = new FormData();
        formData.append("file", file);
        return {
          url: "/api/edge/devices/fulfillment-settings/order-display/background",
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: ["FulfillmentSettings"],
    }),
    deleteOrderDisplayBackground: builder.mutation({
      query: () => ({
        url: "/api/edge/devices/fulfillment-settings/order-display/background",
        method: "DELETE",
      }),
      invalidatesTags: ["FulfillmentSettings"],
    }),
    uploadKitchenDisplayBackground: builder.mutation({
      query: (file) => {
        const formData = new FormData();
        formData.append("file", file);
        return {
          url: "/api/edge/devices/fulfillment-settings/kitchen-display/background",
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: ["FulfillmentSettings"],
    }),
    uploadAttractBannerImage: builder.mutation({
      query: (file) => {
        const formData = new FormData();
        formData.append("file", file);
        return {
          url: "/api/edge/devices/fulfillment-settings/attract-banners/upload",
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: ["FulfillmentSettings"],
    }),
    deleteKitchenDisplayBackground: builder.mutation({
      query: () => ({
        url: "/api/edge/devices/fulfillment-settings/kitchen-display/background",
        method: "DELETE",
      }),
      invalidatesTags: ["FulfillmentSettings"],
    }),
    getFulfillmentOrders: builder.query({
      query: ({
        status,
        from,
        to,
        search,
        tokenStatus,
        stationCode,
        page,
        pageSize,
        limit,
      } = {}) => {
        const params = new URLSearchParams();
        if (status) params.append("status", status);
        if (from) params.append("from", from);
        if (to) params.append("to", to);
        if (search) params.append("search", search);
        if (tokenStatus) params.append("tokenStatus", tokenStatus);
        if (stationCode) params.append("stationCode", stationCode);
        if (page) params.append("page", page);
        if (pageSize) params.append("pageSize", pageSize);
        if (limit) params.append("limit", limit);
        const queryString = params.toString();
        return queryString
          ? `/admin/fulfillment/orders?${queryString}`
          : "/admin/fulfillment/orders";
      },
      providesTags: ["FulfillmentOrder"],
    }),
    exportFulfillmentOrders: builder.mutation({
      query: ({ status, search, from, to, tokenStatus, stationCode } = {}) => {
        const params = new URLSearchParams();
        if (status) params.append("status", status);
        if (search) params.append("search", search);
        if (from) params.append("from", from);
        if (to) params.append("to", to);
        if (tokenStatus) params.append("tokenStatus", tokenStatus);
        if (stationCode) params.append("stationCode", stationCode);
        const queryString = params.toString();

        return {
          url: queryString
            ? `/admin/fulfillment/orders/export?${queryString}`
            : "/admin/fulfillment/orders/export",
          method: "GET",
          responseHandler: (response) => response.blob(),
        };
      },
    }),
    exportFulfillmentOrderItems: builder.mutation({
      query: ({ status, search, from, to, tokenStatus, stationCode } = {}) => {
        const params = new URLSearchParams();
        if (status) params.append("status", status);
        if (search) params.append("search", search);
        if (from) params.append("from", from);
        if (to) params.append("to", to);
        if (tokenStatus) params.append("tokenStatus", tokenStatus);
        if (stationCode) params.append("stationCode", stationCode);
        const queryString = params.toString();

        return {
          url: queryString
            ? `/admin/fulfillment/orders/items/export?${queryString}`
            : "/admin/fulfillment/orders/items/export",
          method: "GET",
          responseHandler: (response) => response.blob(),
        };
      },
    }),
    getFulfillmentOrder: builder.query({
      query: (orderId) => `/admin/fulfillment/orders/${orderId}`,
      providesTags: (result, error, orderId) => [
        { type: "FulfillmentOrder", id: orderId },
      ],
    }),

    // Reporting - Sales Summary
    getSalesSummary: builder.query({
      query: ({ from, to, channel } = {}) => {
        const params = new URLSearchParams();
        if (from) params.append("from", from);
        if (to) params.append("to", to);
        if (channel) params.append("channel", channel);
        return `/api/edge/reports/sales-summary?${params.toString()}`;
      },
      providesTags: ["Report"],
    }),

    // Reporting - Payment Performance
    getPaymentPerformance: builder.query({
      query: ({ from, to, channel } = {}) => {
        const params = new URLSearchParams();
        if (from) params.append("from", from);
        if (to) params.append("to", to);
        if (channel) params.append("channel", channel);
        return `/api/edge/reports/payment-performance?${params.toString()}`;
      },
      providesTags: ["Report"],
    }),

    // Reporting - Order Lifecycle
    getOrderLifecycle: builder.query({
      query: ({ from, to, channel } = {}) => {
        const params = new URLSearchParams();
        if (from) params.append("from", from);
        if (to) params.append("to", to);
        if (channel) params.append("channel", channel);
        return `/api/edge/reports/order-lifecycle?${params.toString()}`;
      },
      providesTags: ["Report"],
    }),

    // Reporting - Hourly Demand
    getHourlyDemand: builder.query({
      query: ({ from, to, channel } = {}) => {
        const params = new URLSearchParams();
        if (from) params.append("from", from);
        if (to) params.append("to", to);
        if (channel) params.append("channel", channel);
        return `/api/edge/reports/hourly-demand?${params.toString()}`;
      },
      providesTags: ["Report"],
    }),

    // Reporting - Top Items & Category Mix
    getTopItemsCategoryMix: builder.query({
      query: ({ from, to, channel, limit } = {}) => {
        const params = new URLSearchParams();
        if (from) params.append("from", from);
        if (to) params.append("to", to);
        if (channel) params.append("channel", channel);
        if (limit) params.append("limit", limit);
        return `/api/edge/reports/top-items-category-mix?${params.toString()}`;
      },
      providesTags: ["Report"],
    }),

    // Upselling - Get All Campaigns
    getUpsellCampaigns: builder.query({
      query: () => "/admin/catalog/upsell-campaigns",
      providesTags: ["UpsellCampaign"],
    }),

    // Upselling - Get Campaign by ID
    getUpsellCampaign: builder.query({
      query: (campaignId) => `/admin/catalog/upsell-campaigns/${campaignId}`,
      providesTags: ["UpsellCampaign"],
    }),

    // Upselling - Create Campaign
    createUpsellCampaign: builder.mutation({
      query: (payload) => ({
        url: "/admin/catalog/upsell-campaigns",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["UpsellCampaign"],
    }),

    // Upselling - Update Campaign
    updateUpsellCampaign: builder.mutation({
      query: ({ campaignId, payload }) => ({
        url: `/admin/catalog/upsell-campaigns/${campaignId}`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["UpsellCampaign"],
    }),

    // Upselling - Activate Campaign
    activateUpsellCampaign: builder.mutation({
      query: (campaignId) => ({
        url: `/admin/catalog/upsell-campaigns/${campaignId}/activate`,
        method: "POST",
      }),
      invalidatesTags: ["UpsellCampaign"],
    }),

    // Upselling - Deactivate Campaign
    deactivateUpsellCampaign: builder.mutation({
      query: (campaignId) => ({
        url: `/admin/catalog/upsell-campaigns/${campaignId}/deactivate`,
        method: "POST",
      }),
      invalidatesTags: ["UpsellCampaign"],
    }),

    // Upselling - Delete Campaign
    deleteUpsellCampaign: builder.mutation({
      query: (campaignId) => ({
        url: `/admin/catalog/upsell-campaigns/${campaignId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["UpsellCampaign"],
    }),

    // ============================================
    // POS DINE-IN ENDPOINTS
    // ============================================

    // POS - Get Seats (Seat Board)
    getPosSeats: builder.query({
      query: ({ floorId, sectionId, seatType } = {}) => {
        const params = new URLSearchParams();
        if (floorId) params.append("floorId", floorId);
        if (sectionId) params.append("sectionId", sectionId);
        if (seatType) params.append("seatType", seatType);
        return `/api/edge/pos/seats?${params.toString()}`;
      },
      providesTags: ["Seat"],
    }),

    // POS - Open Seat
    openPosSeat: builder.mutation({
      query: ({ seatId, payload }) => ({
        url: `/api/edge/pos/seats/${seatId}/open`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["Seat", "PosOrder"],
    }),

    // POS - Seat Heartbeat (keep draft session alive)
    posSeatHeartbeat: builder.mutation({
      query: (seatId) => ({
        url: `/api/edge/pos/seats/${seatId}/heartbeat`,
        method: "POST",
      }),
    }),

    // POS - Release Seat (end device lock without changing order status)
    posSeatRelease: builder.mutation({
      query: (seatId) => ({
        url: `/api/edge/pos/seats/${seatId}/release`,
        method: "POST",
      }),
      invalidatesTags: ["Seat"],
    }),

    // POS - Open Takeaway Order (no seat required)
    openPosTakeaway: builder.mutation({
      query: (payload) => ({
        url: "/api/edge/pos/orders/takeaway/open",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["PosOrder"],
    }),

    // POS - Open Dine-In Order (no table, no seat required)
    openPosDineIn: builder.mutation({
      query: (payload) => ({
        url: "/api/edge/pos/orders/dinein/open",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["PosOrder"],
    }),

    // POS - Update Order Customer Details
    updatePosOrderCustomer: builder.mutation({
      query: ({ orderId, payload }) => ({
        url: `/api/edge/pos/orders/${orderId}/customer`,
        method: "PATCH",
        body: payload,
      }),
      invalidatesTags: (result, error, { orderId }) => [
        { type: "PosOrder", id: orderId },
      ],
    }),

    // POS - Get Order
    getPosOrders: builder.query({
      query: ({
        status,
        channels,
        sourceSystems,
        search,
        from,
        to,
        page,
        pageSize,
      } = {}) => {
        const params = new URLSearchParams();
        if (status) params.append("status", status);
        if (channels) params.append("channels", channels);
        if (sourceSystems) params.append("sourceSystems", sourceSystems);
        if (search) params.append("search", search);
        if (from) params.append("from", from);
        if (to) params.append("to", to);
        if (page) params.append("page", page);
        if (pageSize) params.append("pageSize", pageSize);
        const queryString = params.toString();
        return queryString
          ? `/api/edge/pos/orders?${queryString}`
          : "/api/edge/pos/orders";
      },
      providesTags: ["PosOrder"],
    }),

    // POS - Get Order
    getPosOrder: builder.query({
      query: (orderId) => `/api/edge/pos/orders/${orderId}`,
      providesTags: (result, error, orderId) => [
        { type: "PosOrder", id: orderId },
      ],
    }),

    // POS - Add Order Line
    addPosOrderLine: builder.mutation({
      query: ({ orderId, payload }) => ({
        url: `/api/edge/pos/orders/${orderId}/lines`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["Seat"],
    }),

    // POS - Update Order Line
    updatePosOrderLine: builder.mutation({
      query: ({ orderId, lineId, payload }) => ({
        url: `/api/edge/pos/orders/${orderId}/lines/${lineId}`,
        method: "PATCH",
        body: payload,
      }),
      invalidatesTags: ["Seat"],
    }),

    // POS - Delete Order Line
    deletePosOrderLine: builder.mutation({
      query: ({ orderId, lineId }) => ({
        url: `/api/edge/pos/orders/${orderId}/lines/${lineId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Seat"],
    }),

    // POS - Update Order Notes
    updatePosOrderNotes: builder.mutation({
      query: ({ orderId, payload }) => ({
        url: `/api/edge/pos/orders/${orderId}/notes`,
        method: "PATCH",
        body: payload,
      }),
      invalidatesTags: (result, error, { orderId }) => [
        { type: "PosOrder", id: orderId },
      ],
    }),

    // POS - Apply Discount
    applyPosOrderDiscount: builder.mutation({
      query: ({ orderId, payload }) => ({
        url: `/api/edge/pos/orders/${orderId}/discounts`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: (result, error, { orderId }) => [
        { type: "PosOrder", id: orderId },
        "Seat",
      ],
    }),

    // POS - Remove Discount
    removePosOrderDiscount: builder.mutation({
      query: ({ orderId, discountId }) => ({
        url: `/api/edge/pos/orders/${orderId}/discounts/${discountId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { orderId }) => [
        { type: "PosOrder", id: orderId },
        "Seat",
      ],
    }),

    // POS - Move Seat
    movePosSeat: builder.mutation({
      query: ({ orderId, payload }) => ({
        url: `/api/edge/pos/orders/${orderId}/move-seat`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: (result, error, { orderId }) => [
        { type: "PosOrder", id: orderId },
        "Seat",
      ],
    }),

    // POS - Submit to Kitchen
    submitPosOrder: builder.mutation({
      query: ({ orderId, payload }) => ({
        url: `/api/edge/pos/orders/${orderId}/submit`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: (result, error, { orderId }) => [
        { type: "PosOrder", id: orderId },
        "Seat",
      ],
    }),

    // POS - Close Draft Order (release seat)
    closePosOrder: builder.mutation({
      query: (orderId) => ({
        url: `/api/edge/pos/orders/${orderId}/close`,
        method: "POST",
      }),
      invalidatesTags: (result, error, orderId) => [
        { type: "PosOrder", id: orderId },
        "Seat",
      ],
    }),

    // POS - Split Invoice Equally
    splitPosInvoiceEqually: builder.mutation({
      query: ({ orderId, payload }) => ({
        url: `/api/edge/pos/orders/${orderId}/invoices/split-equally`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: (result, error, { orderId }) => [
        { type: "PosOrder", id: orderId },
        "Invoice",
      ],
    }),

    // POS - Split Preview (unit-level)
    getPosInvoiceSplitPreview: builder.query({
      query: ({ orderId, sourceInvoiceId } = {}) => {
        const params = new URLSearchParams();
        if (sourceInvoiceId) {
          params.append("sourceInvoiceId", sourceInvoiceId);
        }
        const queryString = params.toString();
        return queryString
          ? `/api/edge/pos/orders/${orderId}/invoices/split-preview?${queryString}`
          : `/api/edge/pos/orders/${orderId}/invoices/split-preview`;
      },
      providesTags: (result, error, { orderId, sourceInvoiceId }) => [
        { type: "PosOrder", id: orderId },
        { type: "Invoice", id: sourceInvoiceId || `ORDER_${orderId}` },
      ],
    }),

    // POS - Split Selected Items Into New Invoice
    splitPosInvoiceItems: builder.mutation({
      query: ({ orderId, payload }) => ({
        url: `/api/edge/pos/orders/${orderId}/invoices/split-items`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: (result, error, { orderId }) => [
        { type: "PosOrder", id: orderId },
        "Invoice",
        "Seat",
      ],
    }),

    // POS - Get Order Invoices
    getPosOrderInvoices: builder.query({
      query: (orderId) => `/api/edge/pos/orders/${orderId}/invoices`,
      providesTags: (result, error, orderId) => [
        { type: "Invoice", id: `ORDER_${orderId}` },
      ],
    }),

    // POS - Get Order Payments
    getPosOrderPayments: builder.query({
      query: (orderId) => `/api/edge/pos/orders/${orderId}/payments`,
      providesTags: (result, error, orderId) => [
        "Payment",
        { type: "Payment", id: `ORDER_${orderId}` },
      ],
    }),

    // POS - Get Invoice Payments
    getPosInvoicePayments: builder.query({
      query: (invoiceId) => `/api/edge/pos/invoices/${invoiceId}/payments`,
      providesTags: (result, error, invoiceId) => [
        "Payment",
        { type: "Payment", id: `INVOICE_${invoiceId}` },
      ],
    }),

    // POS - Get Invoice
    getPosInvoice: builder.query({
      query: (invoiceId) => `/api/edge/pos/invoices/${invoiceId}`,
      providesTags: (result, error, invoiceId) => [
        { type: "Invoice", id: invoiceId },
      ],
    }),

    // POS - Print Invoice Receipt
    printPosInvoiceReceipt: builder.mutation({
      query: (invoiceId) => ({
        url: `/api/edge/pos/invoices/${invoiceId}/print/receipt`,
        method: "POST",
      }),
    }),

    // POS - Get Print Job Status
    getPosPrintJob: builder.query({
      query: (printJobId) => `/api/edge/pos/print/jobs/${printJobId}`,
    }),

    // POS - Invoice Payment
    processPosInvoicePayment: builder.mutation({
      query: ({ invoiceId, payload }) => ({
        url: `/api/edge/pos/invoices/${invoiceId}/payments`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: (result, error, { invoiceId }) => [
        { type: "Invoice", id: invoiceId },
        "PosOrder",
        "Seat",
        "TillSession",
        "Reconciliation",
      ],
    }),

    // POS - Order Payment (Full Order)
    processPosOrderPayment: builder.mutation({
      query: ({ orderId, payload }) => ({
        url: `/api/edge/pos/orders/${orderId}/payments`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: (result, error, { orderId }) => [
        { type: "PosOrder", id: orderId },
        "Invoice",
        "Seat",
        "TillSession",
        "Reconciliation",
      ],
    }),

    // POS - Change Payment Method
    updatePosPaymentMethod: builder.mutation({
      query: ({ paymentId, payload }) => ({
        url: `/api/edge/pos/payments/${paymentId}/method`,
        method: "PATCH",
        body: payload,
      }),
      invalidatesTags: (result, error, { paymentId }) => [
        { type: "Payment", id: paymentId },
        "Payment",
        "PosOrder",
        "Invoice",
      ],
    }),
    // POS - Full Refund (local mode / backend simulated mode)
    refundPosOrder: builder.mutation({
      query: ({ orderId, payload }) => ({
        url: `/api/edge/pos/orders/${orderId}/refund`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: (result, error, { orderId }) => [
        { type: "PosOrder", id: orderId },
        "Invoice",
        "Seat",
      ],
    }),
    // POS - Cash refund by the customer-facing order number
    cashRefundPosOrder: builder.mutation({
      query: ({ orderNo, payload }) => ({
        url: `/api/edge/pos/orders/${encodeURIComponent(orderNo)}/refund/cash`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: (result, error, { orderId }) => [
        "FulfillmentOrder",
        "Payment",
        "TillSession",
        "Reconciliation",
        "PosOrder",
        ...(orderId ? [{ type: "PosOrder", id: orderId }] : []),
        ...(orderId ? [{ type: "Payment", id: `ORDER_${orderId}` }] : []),
      ],
    }),
    // POS - Void Sale (same-day)
    voidPosOrder: builder.mutation({
      query: ({ orderId, payload }) => ({
        url: `/api/edge/pos/orders/${orderId}/void`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: (result, error, { orderId }) => [
        { type: "PosOrder", id: orderId },
        "Invoice",
        "Seat",
      ],
    }),
    // POS - Return Void (void prior refund)
    returnVoidPosOrder: builder.mutation({
      query: ({ orderId, payload }) => ({
        url: `/api/edge/pos/orders/${orderId}/return-void`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: (result, error, { orderId }) => [
        { type: "PosOrder", id: orderId },
        "Invoice",
        "Seat",
      ],
    }),
  }),
});

export const {
  useLoginWithPinMutation,
  useGetEdgeCustomerLookupQuery,
  useLazyGetEdgeCustomerLookupQuery,
  useCreateRealtimeSessionMutation,
  useGetRealtimeSyncQuery,
  useGetEdgeStoreSetupQuery,
  useGetStoreFeaturesQuery,
  useUpdateStoreFeaturesMutation,
  useConfigureEdgeStoreMutation,
  useBridgePaymentCallbackMutation,
  useBridgePrintCallbackMutation,
  useGetReservationsQuery,
  useGetReservationSlotsQuery,
  useGetReservationByCodeQuery,
  useGetEdgeCustomersQuery,
  useCreateReservationMutation,
  useUpdateReservationMutation,
  useAssignReservationTableMutation,
  useCheckInReservationMutation,
  useMarkNoShowReservationMutation,
  useMarkUsedReservationMutation,
  useCancelReservationMutation,
  useGetCategoriesQuery,
  useGetStationsQuery,
  useCreateStationMutation,
  useUpdateStationMutation,
  useDeactivateStationMutation,
  useGetPrintersQuery,
  useGetPublicCatalogMenuQuery,
  useCreatePrinterMutation,
  useUpdatePrinterMutation,
  useDeletePrinterMutation,
  useCreateCategoryMutation,
  useUpdateCategoryMutation,
  useDeleteCategoryMutation,
  useGetItemsQuery,
  useCreateItemMutation,
  useUpdateItemMutation,
  usePatchItemAvailabilityMutation,
  usePatchCategorySortOrderMutation,
  usePatchItemSortOrderMutation,
  useAssignItemStationMutation,
  useBatchAssignItemStationsMutation,
  useDeleteItemMutation,
  useGetMenuQuery,
  useGetCatalogReferenceQuery,
  useGetUsersQuery,
  useGetUserQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useGetAccessLevelsQuery,
  useCreateAccessLevelMutation,
  useUpdateAccessLevelMutation,
  useDeleteAccessLevelMutation,
  useGetFloorsQuery,
  useCreateFloorMutation,
  useUpdateFloorMutation,
  useDeleteFloorMutation,
  useGetSectionsQuery,
  useCreateSectionMutation,
  useUpdateSectionMutation,
  useDeleteSectionMutation,
  useGetTablesQuery,
  useCreateTableMutation,
  useUpdateTableMutation,
  useDeleteTableMutation,
  useGetTaxRatesQuery,
  useCreateTaxRateMutation,
  useUpdateTaxRateMutation,
  useDeleteTaxRateMutation,
  useGetLocalDevicesQuery,
  useGetDeviceHealthQuery,
  usePostDeviceHeartbeatMutation,
  useCreateLocalDeviceMutation,
  useUpdateLocalDeviceMutation,
  useDeleteLocalDeviceMutation,
  useGetKiosksQuery,
  useCreateKioskMutation,
  useUpdateKioskMutation,
  useDeleteKioskMutation,
  useUnlockKioskMutation,
  useLockKioskMutation,
  useGetKioskStatusQuery,
  useGetTerminalsQuery,
  useCreateTerminalMutation,
  useUpdateTerminalMutation,
  useDeleteTerminalMutation,
  useAssignTerminalToKioskMutation,
  useGetTillsQuery,
  useGetTillQuery,
  useCreateTillMutation,
  useUpdateTillMutation,
  useDeactivateTillMutation,
  useGetOpenTillSessionsQuery,
  useGetEdgeShiftHistorySessionsQuery,
  useOpenTillSessionMutation,
  useGetTillSessionQuery,
  useGetCashMovementsQuery,
  useCreateCashMovementMutation,
  useUpdateCashMovementMutation,
  useDeleteCashMovementMutation,
  useApproveCashMovementMutation,
  useRejectCashMovementMutation,
  useCloseTillSessionMutation,
  useApproveTillSessionMutation,
  useReopenTillSessionMutation,
  useStartCashUpMutation,
  useSubmitCashCountMutation,
  useGetReconciliationQuery,
  useRecalculateReconciliationMutation,
  useSaveReconciliationLinesMutation,
  useGetDayCloseQuery,
  usePrepareDayCloseMutation,
  useCloseDayCloseMutation,
  useReopenDayCloseMutation,
  usePairDeviceMutation,
  useGetFulfillmentSettingsQuery,
  useUpdateFulfillmentSettingsMutation,
  useUploadOrderDisplayBackgroundMutation,
  useDeleteOrderDisplayBackgroundMutation,
  useUploadKitchenDisplayBackgroundMutation,
  useUploadAttractBannerImageMutation,
  useDeleteKitchenDisplayBackgroundMutation,
  useGetFulfillmentOrdersQuery,
  useLazyGetFulfillmentOrdersQuery,
  useExportFulfillmentOrdersMutation,
  useExportFulfillmentOrderItemsMutation,
  useGetFulfillmentOrderQuery,
  // Reporting hooks
  useGetSalesSummaryQuery,
  useGetPaymentPerformanceQuery,
  useGetOrderLifecycleQuery,
  useGetHourlyDemandQuery,
  useGetTopItemsCategoryMixQuery,
  // Upselling hooks
  useGetUpsellCampaignsQuery,
  useGetUpsellCampaignQuery,
  useCreateUpsellCampaignMutation,
  useUpdateUpsellCampaignMutation,
  useActivateUpsellCampaignMutation,
  useDeactivateUpsellCampaignMutation,
  useDeleteUpsellCampaignMutation,
  // POS Dine-In hooks
  useGetPosSeatsQuery,
  useOpenPosSeatMutation,
  usePosSeatHeartbeatMutation,
  usePosSeatReleaseMutation,
  useOpenPosTakeawayMutation,
  useOpenPosDineInMutation,
  useUpdatePosOrderCustomerMutation,
  useGetPosOrdersQuery,
  useLazyGetPosOrdersQuery,
  useGetPosOrderQuery,
  useLazyGetPosOrderQuery,
  useAddPosOrderLineMutation,
  useUpdatePosOrderLineMutation,
  useDeletePosOrderLineMutation,
  useUpdatePosOrderNotesMutation,
  useApplyPosOrderDiscountMutation,
  useRemovePosOrderDiscountMutation,
  useMovePosSeatMutation,
  useSubmitPosOrderMutation,
  useClosePosOrderMutation,
  useSplitPosInvoiceEquallyMutation,
  useGetPosInvoiceSplitPreviewQuery,
  useSplitPosInvoiceItemsMutation,
  useGetPosOrderInvoicesQuery,
  useLazyGetPosOrderInvoicesQuery,
  useGetPosOrderPaymentsQuery,
  useLazyGetPosOrderPaymentsQuery,
  useGetPosInvoicePaymentsQuery,
  useGetPosInvoiceQuery,
  usePrintPosInvoiceReceiptMutation,
  useGetPosPrintJobQuery,
  useLazyGetPosPrintJobQuery,
  useProcessPosInvoicePaymentMutation,
  useProcessPosOrderPaymentMutation,
  useUpdatePosPaymentMethodMutation,
  useRefundPosOrderMutation,
  useCashRefundPosOrderMutation,
  useVoidPosOrderMutation,
  useReturnVoidPosOrderMutation,
} = api;
